/** Formatação pt-BR compartilhada entre backend (PDF) e frontend. */
export const fmtBRL = (n) =>
  (Number(n) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const fmtPct = (n) =>
  ((Number(n) || 0) * 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + '%';

export const fmtNum = (n, d = 2) =>
  (Number(n) || 0).toLocaleString('pt-BR', { minimumFractionDigits: d, maximumFractionDigits: d });

export function fmtDateTime(iso) {
  const d = new Date(iso);
  return (
    d.toLocaleDateString('pt-BR') +
    ' às ' +
    d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  );
}

export function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso.length === 10 ? iso + 'T12:00:00' : iso);
  return d.toLocaleDateString('pt-BR');
}

export function maskCNPJ(v) {
  const d = String(v || '').replace(/\D/g, '').slice(0, 14);
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

/** Máscara de CPF — 000.000.000-00. */
export function maskCPF(v) {
  const d = String(v || '').replace(/\D/g, '').slice(0, 11);
  return d
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1-$2');
}

/**
 * Máscara do documento do cliente, que pode ser pessoa jurídica ou física:
 * até 11 dígitos formata como CPF, acima disso como CNPJ. É a máscara a usar em
 * qualquer campo de documento de cliente — `maskCNPJ` continua valendo só onde
 * o documento é obrigatoriamente de pessoa jurídica (a empresa, em Ajustes).
 */
export function maskDocumento(v) {
  const d = String(v || '').replace(/\D/g, '').slice(0, 14);
  return d.length <= 11 ? maskCPF(d) : maskCNPJ(d);
}

/** 'cpf' (11 dígitos), 'cnpj' (14) ou null enquanto o documento está incompleto. */
export function tipoDocumento(v) {
  const d = String(v || '').replace(/\D/g, '');
  if (d.length === 11) return 'cpf';
  if (d.length === 14) return 'cnpj';
  return null;
}

/** Rótulo do documento para exibição — "CPF" ou "CNPJ" (o padrão). */
export function labelDocumento(v) {
  return tipoDocumento(v) === 'cpf' ? 'CPF' : 'CNPJ';
}

export function maskPhone(v) {
  const d = String(v || '').replace(/\D/g, '').slice(0, 11);
  if (d.length <= 10) return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2');
  return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2');
}

/** Valida CNPJ pelos dígitos verificadores. */
export function isValidCNPJ(v) {
  const c = String(v || '').replace(/\D/g, '');
  if (c.length !== 14 || /^(\d)\1{13}$/.test(c)) return false;
  const calc = (len) => {
    let sum = 0;
    let pos = len - 7;
    for (let i = 0; i < len; i++) {
      sum += Number(c[i]) * pos--;
      if (pos < 2) pos = 9;
    }
    const r = sum % 11;
    return r < 2 ? 0 : 11 - r;
  };
  return calc(12) === Number(c[12]) && calc(13) === Number(c[13]);
}

/** Valida CPF pelos dígitos verificadores. */
export function isValidCPF(v) {
  const c = String(v || '').replace(/\D/g, '');
  if (c.length !== 11 || /^(\d)\1{10}$/.test(c)) return false;
  const calc = (len) => {
    let sum = 0;
    for (let i = 0; i < len; i++) sum += Number(c[i]) * (len + 1 - i);
    const r = (sum * 10) % 11;
    return r === 10 ? 0 : r;
  };
  return calc(9) === Number(c[9]) && calc(10) === Number(c[10]);
}

/**
 * Valida o documento do cliente: CPF quando tem 11 dígitos, CNPJ quando tem 14.
 * Qualquer outro tamanho é inválido — é o que os cadastros de cliente e o
 * bloco "Cliente" da proposta usam no lugar de `isValidCNPJ`.
 */
export function isValidDocumento(v) {
  const tipo = tipoDocumento(v);
  if (tipo === 'cpf') return isValidCPF(v);
  if (tipo === 'cnpj') return isValidCNPJ(v);
  return false;
}
